/**
 * AdminKTTriggersPage — `/admin/kt-triggers`
 *
 * Admin/hr tooling to manage the keyword-trigger list + audit the detections.
 *
 * Left: keyword list (enable/disable, edit, delete, add new).
 * Right: signals audit log (who said what, when, severity, link to the KT workflow).
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { AlertCircle, Plus, Trash2, Edit3, Save, X, Radio, Activity, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const SEVERITY_META = {
  high:   { color: '#FF3B30', bg: 'bg-[#FF3B30]/10', label: 'High' },
  medium: { color: '#FF9500', bg: 'bg-[#FF9500]/10', label: 'Medium' },
  low:    { color: '#9CA3AF', bg: 'bg-[#9CA3AF]/10', label: 'Low' },
};

const SeverityBadge = ({ sev }) => {
  const m = SEVERITY_META[sev] || SEVERITY_META.low;
  return <Badge style={{ color: m.color, backgroundColor: `${m.color}15` }} className="text-[10px]">{m.label}</Badge>;
};

const AdminKTTriggersPage = () => {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [triggers, setTriggers] = useState([]);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [newDraft, setNewDraft] = useState({ keyword: '', severity: 'medium', label: '', enabled: true });

  const isAdmin = user?.role === 'admin' || user?.role === 'hr';

  const fetchAll = async () => {
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/kt-triggers`, { headers }),
        axios.get(`${API}/kt-signals`, { headers }),
      ]);
      setTriggers(t.data.triggers || []);
      setSignals(s.data.signals || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load triggers');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) fetchAll(); /* eslint-disable-next-line */ }, [isAdmin]);

  const addTrigger = async () => {
    if (!newDraft.keyword.trim()) return toast.error('Keyword required');
    try {
      await axios.post(`${API}/kt-triggers`, newDraft, { headers });
      setNewDraft({ keyword: '', severity: 'medium', label: '', enabled: true });
      toast.success('Trigger added');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add trigger');
    }
  };

  const patchTrigger = async (trigger_id, patch) => {
    try {
      await axios.put(`${API}/kt-triggers/${trigger_id}`, patch, { headers });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const saveEdit = async () => {
    if (!editDraft.keyword?.trim()) return toast.error('Keyword required');
    await patchTrigger(editingId, editDraft);
    setEditingId(null);
    setEditDraft({});
  };

  const deleteTrigger = async (trigger_id) => {
    if (!window.confirm('Delete this trigger? Signals already detected remain in the audit log.')) return;
    try {
      await axios.delete(`${API}/kt-triggers/${trigger_id}`, { headers });
      toast.success('Trigger deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (!isAdmin) {
    return <div className="p-6 text-sm text-[#6B7280]">Admin or HR role required.</div>;
  }
  if (loading) return <div className="p-6 text-sm text-[#9CA3AF]">Loading…</div>;

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-[1200px]" data-testid="admin-kt-triggers-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#002FA7]/10 flex items-center justify-center">
          <Radio className="w-5 h-5 text-[#002FA7]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" data-testid="page-title">KT Triggers</h1>
          <p className="text-sm text-[#6B7280]">Keywords that auto-launch a Knowledge Transfer workflow when detected in chats, notes, or tasks.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {/* Left: triggers list */}
        <div className="lg:col-span-3 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Radio className="w-4 h-4 text-[#002FA7]" />
                Keyword triggers
                <Badge className="bg-[#002FA7]/10 text-[#002FA7] text-[10px] ml-1">{triggers.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {triggers.map(t => (
                <div key={t.trigger_id}
                     className={`flex items-center gap-3 border rounded-lg px-3 py-2 ${t.enabled ? 'bg-white' : 'bg-[#F9FAFB]'}`}
                     data-testid={`trigger-${t.trigger_id}`}>
                  {editingId === t.trigger_id ? (
                    <div className="flex-1 flex flex-wrap gap-2 items-center">
                      <Input value={editDraft.keyword} onChange={e => setEditDraft(d => ({ ...d, keyword: e.target.value }))} className="h-8 w-40" />
                      <select value={editDraft.severity} onChange={e => setEditDraft(d => ({ ...d, severity: e.target.value }))} className="h-8 text-xs border rounded px-2">
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                      <Input value={editDraft.label} onChange={e => setEditDraft(d => ({ ...d, label: e.target.value }))} placeholder="Label" className="h-8 flex-1 min-w-[140px]" />
                      <Button size="sm" onClick={saveEdit} className="bg-[#002FA7] h-7" data-testid={`save-${t.trigger_id}`}><Save className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditDraft({}); }} className="h-7"><X className="w-3 h-3" /></Button>
                    </div>
                  ) : (
                    <>
                      <code className="font-mono text-sm font-semibold text-[#111827] w-40 truncate">{t.keyword}</code>
                      <SeverityBadge sev={t.severity} />
                      <span className="text-xs text-[#6B7280] flex-1 truncate">{t.label}</span>
                      {t.is_default && <Badge className="bg-[#F3F4F6] text-[#6B7280] text-[9px]">default</Badge>}
                      <Switch checked={t.enabled} onCheckedChange={(v) => patchTrigger(t.trigger_id, { enabled: v })} data-testid={`toggle-${t.trigger_id}`} />
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(t.trigger_id); setEditDraft({ keyword: t.keyword, severity: t.severity, label: t.label, enabled: t.enabled }); }} className="h-7 w-7 p-0" data-testid={`edit-${t.trigger_id}`}>
                        <Edit3 className="w-3.5 h-3.5 text-[#6B7280]" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteTrigger(t.trigger_id)} className="h-7 w-7 p-0" data-testid={`delete-${t.trigger_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-[#FF3B30]" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
              {triggers.length === 0 && (
                <p className="text-xs text-[#9CA3AF] text-center py-4">No triggers yet — add your first below.</p>
              )}
            </CardContent>
          </Card>

          {/* Add new */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><Plus className="w-3.5 h-3.5" />Add new trigger</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-4 gap-2">
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Keyword</Label>
                  <Input value={newDraft.keyword} onChange={e => setNewDraft(d => ({ ...d, keyword: e.target.value }))} placeholder="e.g. sabbatical" data-testid="new-trigger-keyword" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Severity</Label>
                  <select value={newDraft.severity} onChange={e => setNewDraft(d => ({ ...d, severity: e.target.value }))} className="w-full h-10 border rounded-md px-3 text-sm" data-testid="new-trigger-severity">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#6B7280]">Label</Label>
                  <Input value={newDraft.label} onChange={e => setNewDraft(d => ({ ...d, label: e.target.value }))} placeholder="Short description" data-testid="new-trigger-label" />
                </div>
                <div className="flex items-end">
                  <Button onClick={addTrigger} className="bg-[#002FA7] w-full" data-testid="add-trigger-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: signals audit log */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#FF3B30]" />
                Recent signals
                <Badge className="bg-[#FF3B30]/10 text-[#FF3B30] text-[10px] ml-1">{signals.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[700px] overflow-y-auto">
              {signals.length === 0 && (
                <p className="text-xs text-[#9CA3AF] text-center py-6">No KT signals yet — the system will log detections here.</p>
              )}
              {signals.map(s => (
                <div key={s.signal_id} className="border rounded-lg p-2.5 space-y-1" data-testid={`signal-${s.signal_id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <SeverityBadge sev={s.severity} />
                    <span className="text-xs font-semibold text-[#111827]">{s.user_name}</span>
                    <Badge className="bg-[#F3F4F6] text-[#4B5563] text-[9px]">{s.source}</Badge>
                    <span className="text-[10px] text-[#9CA3AF] ml-auto">{new Date(s.detected_at).toLocaleString()}</span>
                  </div>
                  <p className="text-[11px] text-[#4B5563] italic">"{s.text_snippet}"</p>
                  <div className="flex items-center gap-2 pt-0.5 flex-wrap">
                    {s.matched_keywords?.map(k => (
                      <code key={k} className="text-[9px] bg-[#FF3B30]/10 text-[#FF3B30] px-1.5 py-0.5 rounded font-mono">{k}</code>
                    ))}
                    {s.kt_id && (
                      <a href={`/knowledge-transfer`} className="text-[10px] text-[#002FA7] hover:underline ml-auto flex items-center gap-0.5">
                        View KT <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-[#002FA7]/5 to-[#AF52DE]/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-[#002FA7] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-[#0A0A0A] mb-1">How detection works</p>
                  <ul className="text-[10px] text-[#4B5563] space-y-0.5 list-disc list-inside">
                    <li>Chat messages, PKM notes, and task titles are scanned on creation</li>
                    <li>Matched users + all admin/hr get a notification</li>
                    <li>A draft Knowledge Transfer workflow is auto-created (status=pending)</li>
                    <li>Duplicate hits per (user, source, day) are deduped</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminKTTriggersPage;
